import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { useI18n } from "@/contexts/I18nContext";

/* ─── Footer column data (labels translated at render time) ─── */
const PLATFORMA = (t) => [
  { label: "PMS",             href: "/pms" },
  { label: t.platformMenu.bookingEngine,  href: "/booking-engine" },
  { label: t.platformMenu.channelManager, href: "/channel-manager" },
  { label: t.platformMenu.integrations,   href: "/integrimet" },
  { label: t.nav.pricing,     href: "/cmimet" },
];

const FUNKSIONALITETET = (lang) => (lang === "en"
  ? [
      { label: "Reservations",           href: "/pms" },
      { label: "Billing & Fiscalization", href: "/pms" },
      { label: "Reports & Analytics",    href: "/revenue-management" },
      { label: "Smart Hotel",            href: "/integrimet" },
      { label: "Online Check-In",        href: "/platforma" },
    ]
  : [
      { label: "Rezervime",            href: "/pms" },
      { label: "Faturim & Fiskalizim", href: "/pms" },
      { label: "Raporte & Analiza",    href: "/revenue-management" },
      { label: "Smart Hotel",          href: "/integrimet" },
      { label: "Online Check-In",      href: "/platforma" },
    ]);

const BURIME = (lang) => (lang === "en"
  ? [
      { label: "Blog",             href: "/blog" },
      { label: "Guides",           href: "/blog" },
      { label: "Webinars",         href: "/blog" },
      { label: "Download",         href: "/desktop" },
      { label: "Help Center",      href: "/kontakt" },
    ]
  : [
      { label: "Blog",              href: "/blog" },
      { label: "Udhëzues",          href: "/blog" },
      { label: "Webinare",          href: "/blog" },
      { label: "Shkarko",           href: "/desktop" },
      { label: "Qendra e Ndihmës",  href: "/kontakt" },
    ]);

const KOMPANIA = (t, lang) => (lang === "en"
  ? [
      { label: t.nav.about,   href: "/rreth-nesh" },
      { label: "Careers",     href: "/kontakt" },
      { label: "Partners",    href: "/kontakt" },
      { label: t.nav.contact, href: "/kontakt" },
    ]
  : [
      { label: "Rreth Nesh", href: "/rreth-nesh" },
      { label: "Karriera",   href: "/kontakt" },
      { label: "Partnerë",   href: "/kontakt" },
      { label: "Kontaktoni", href: "/kontakt" },
    ]);

const SOCIALS = [
  { fa: "fa-facebook-f",   href: "https://facebook.com/akomoda",         label: "Facebook"  },
  { fa: "fa-instagram",    href: "https://instagram.com/akomoda",        label: "Instagram" },
  { fa: "fa-linkedin-in",  href: "https://linkedin.com/company/akomoda", label: "LinkedIn"  },
  { fa: "fa-youtube",      href: "https://youtube.com/@akomoda",         label: "YouTube"   },
];

/* ─── Reusable column ─── */
function Column({ title, items }) {
  return (
    <div>
      <h3 className="font-display text-[12.5px] font-semibold uppercase tracking-[0.16em] text-white/90">
        {title}
      </h3>
      <span
        className="mt-3 block h-px w-10 bg-gradient-to-r from-[#FF7A00] to-[#FF7A00]/0"
        aria-hidden
      />
      <ul className="mt-4 space-y-3">
        {items.map((item) => (
          <li key={item.label}>
            <Link
              to={item.href}
              className="group relative inline-flex items-center text-[13.5px] text-white/55 transition-colors hover:text-white"
            >
              <span>{item.label}</span>
              <span
                className="ml-0 inline-block h-px w-0 bg-[#FF7A00] transition-all group-hover:ml-2 group-hover:w-4"
                aria-hidden
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { t, lang } = useI18n();
  const platforma = PLATFORMA(t);
  const funksionalitetet = FUNKSIONALITETET(lang);
  const burime = BURIME(lang);
  const kompania = KOMPANIA(t, lang);
  const legalItems = lang === "en"
    ? [
        { label: "Terms of Use", href: "/kushtet" },
        { label: "Privacy",       href: "/privatesia" },
        { label: "Cookies Policy",href: "/cookies" },
      ]
    : [
        { label: "Kushtet e Përdorimit", href: "/kushtet" },
        { label: "Privatësia",            href: "/privatesia" },
        { label: "Politikat e Cookies",  href: "/cookies" },
      ];
  const brandBlurb = lang === "en"
    ? "A modern platform to manage hotels, reservations and daily operations in one unified system."
    : "Platformë moderne për menaxhimin e hoteleve, rezervimeve dhe operacioneve në një sistem të vetëm.";
  return (
    <footer
      className="relative isolate overflow-hidden bg-[#071326] text-white"
      data-testid="site-footer"
    >
      {/* Cinematic gradient backdrop */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,#0F2647_0%,#071326_55%,#040A18_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -top-32 right-[10%] h-[460px] w-[460px] rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.18),transparent_70%)] blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -bottom-20 left-[10%] h-[320px] w-[320px] rounded-full bg-[radial-gradient(closest-side,rgba(56,109,212,0.16),transparent_70%)] blur-3xl"
        aria-hidden
      />
      {/* Top edge highlight */}
      <span
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
        aria-hidden
      />

      <div className="container-akm relative">
        {/* Main grid */}
        <div className="grid gap-12 py-16 md:py-20 lg:grid-cols-12 lg:gap-10">
          {/* ─── Brand block (col-span 4 on lg) ─── */}
          <div className="lg:col-span-4">
            <Link
              to="/"
              className="inline-flex items-center"
              data-testid="footer-logo"
            >
              <img
                src="/akomoda-logo-white.png"
                alt="Akomoda"
                className="h-9 w-auto object-contain"
                decoding="async"
              />
            </Link>

            <p className="mt-5 max-w-sm text-[13.5px] leading-[1.7] text-white/55">
              {brandBlurb}
            </p>

            {/* Social icons (FontAwesome filled brands) */}
            <div className="mt-8 flex items-center gap-2.5" data-testid="footer-socials">
              {SOCIALS.map(({ fa, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="group relative grid h-10 w-10 place-items-center rounded-full bg-white/[0.06] text-white/80 transition-all hover:bg-[#FF7A00] hover:text-white hover:shadow-[0_0_18px_rgba(255,122,0,0.45)]"
                >
                  <i className={`fa-brands ${fa} text-[15px]`} aria-hidden></i>
                </a>
              ))}
            </div>
          </div>

          {/* ─── Link columns ─── */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 lg:col-span-8 lg:gap-8">
            <Column title={t.footer.platform}        items={platforma} />
            <Column title={lang === "en" ? "Features" : "Funksionalitetet"} items={funksionalitetet} />
            <Column title={lang === "en" ? "Resources" : "Burime"}          items={burime} />
            <Column title={t.footer.company}         items={kompania} />
          </div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* Bottom bar */}
        <div className="flex flex-col items-start gap-4 py-6 text-[12px] sm:flex-row sm:items-center sm:justify-between">
          <p className="text-white/45">
            © 2026 Akomoda. {t.footer.rights}
          </p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {legalItems.map(({ label, href }) => (
              <li key={label}>
                <Link
                  to={href}
                  className="text-white/55 transition-colors hover:text-white"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
